/**
 * Sales Negotiation — Character Manifest Barrel
 *
 * Import this single file in VoiceRoom:
 *   import { ALL_CHARACTERS } from '@features/sales-negotiation/data';
 *
 * If you need scenario-specific routing:
 *   import { SCENARIOS } from '@features/sales-negotiation/data';
 *   const chars = SCENARIOS['cold-call-mastery'];
 */

import { coldCallMastery } from './coldCallMastery.js';
import { objectionDefense } from './objectionDefense.js';
import { negotiationTactics } from './negotiationTactics.js';
import { enterpriseClosing } from './enterpriseClosing.js';
import { SCENARIO_STATUS, isScenarioLive, COMING_SOON_MESSAGE, SCENARIO_TITLES } from './scenarioStatus.js';

// Which scenarios are open (see scenarioStatus.js). The characters of a coming-soon scenario stay in ALL_CHARACTERS and SCENARIOS;
// only the helpers that hand out a character to play skip them.
export { SCENARIO_STATUS, isScenarioLive, COMING_SOON_MESSAGE, SCENARIO_TITLES };

/** Flat merge — use this for VoiceRoom lookups by character ID. */
export const ALL_CHARACTERS = {
  ...coldCallMastery,
  ...objectionDefense,
  ...negotiationTactics,
  ...enterpriseClosing,
};

/** Scenario buckets — use this if you want category-scoped selection UI. */
export const SCENARIOS = {
  'cold-call-mastery':  coldCallMastery,
  'objection-defense':  objectionDefense,
  'negotiation-tactics': negotiationTactics,
  'enterprise-closing': enterpriseClosing,
};

/** Helpers */
export const getCharacter = (id) => ALL_CHARACTERS[id] ?? null;

/** A random character to play: from one scenario, or from every LIVE scenario. A coming-soon scenario gives none. */
export const getRandomCharacter = (scenario = null) => {
  if (scenario && !isScenarioLive(scenario)) return null;
  const pool = scenario
    ? Object.values(SCENARIOS[scenario] ?? {})
    : Object.entries(SCENARIOS).filter(([key]) => isScenarioLive(key)).flatMap(([, chars]) => Object.values(chars));
  if (!pool.length) return null;
  return pool[Math.floor(Math.random() * pool.length)];
};

/** Which scenario a character belongs to ('cold-call-mastery' ...), or null if it is in none. */
export const getScenarioOf = (characterId) =>
  Object.keys(SCENARIOS).find((key) => Object.prototype.hasOwnProperty.call(SCENARIOS[key], characterId)) ?? null;

/**
 * Path (relative to the sales-negotiation API) for "give me a different character": stays inside the
 * current character's scenario and leaves that character out. With no current character (the Voice
 * Room opened with no id, e.g. from the Dashboard) it is a random pick across every scenario.
 */
export const nextCharacterPath = (currentCharacterId) => {
  const params = new URLSearchParams();
  const scenario = currentCharacterId ? getScenarioOf(currentCharacterId) : null;
  if (scenario) params.set('scenario', scenario);
  if (currentCharacterId) params.set('exclude', currentCharacterId);
  const qs = params.toString();
  return `/characters/random${qs ? `?${qs}` : ''}`;
};

export const getCharactersByDifficulty = (difficulty) =>
  Object.values(ALL_CHARACTERS).filter(c => c.difficulty === difficulty);

export const getCharactersByScenario = (scenarioKey) =>
  Object.values(SCENARIOS[scenarioKey] ?? {});

/** Total counts — useful for stats */
export const CHARACTER_COUNTS = {
  'cold-call-mastery':  Object.keys(coldCallMastery).length,
  'objection-defense':  Object.keys(objectionDefense).length,
  'negotiation-tactics': Object.keys(negotiationTactics).length,
  'enterprise-closing': Object.keys(enterpriseClosing).length,
  total: 0, // computed below
};
CHARACTER_COUNTS.total = Object.values(CHARACTER_COUNTS)
  .filter(v => typeof v === 'number' && v !== 0)
  .reduce((a, b) => a + b, 0);

// Characters people can play today: the sum over LIVE scenarios (85 while two scenarios are coming soon).
// `total` stays the number of characters in the code.
CHARACTER_COUNTS.live = Object.entries(SCENARIOS)
  .filter(([key]) => isScenarioLive(key))
  .reduce((sum, [, chars]) => sum + Object.keys(chars).length, 0);
